import { Children, type ReactNode } from "react";
import classNames from "classnames";
import ListItem, { ListItemProps } from "../ListItem/ListItem";
import IconButton from "../IconButton";

export interface SelectOptions extends ListItemProps {}

export interface DetailsCardProps {
  heading?: string;
  caption?: string;
  className?: string;
  options?: SelectOptions[];
  onEdit?: () => void;
  skipRenderWithEmptyOptions?: boolean;
  editingSection?: string | null;
  actionButtons?: ReactNode;
  renderEdit?: () => ReactNode;
  compact?: boolean;
  linked?: boolean;
  children?: ReactNode;
  testId?: string;
}

export default function DetailsCard({
  className = "",
  heading,
  caption,
  options = [],
  onEdit,
  skipRenderWithEmptyOptions = false,
  editingSection,
  actionButtons,
  renderEdit,
  compact = false,
  linked = false,
  children,
  testId = "details-card"
}: DetailsCardProps) {
  const hasChildren = Children.toArray(children).length > 0;
  if (skipRenderWithEmptyOptions && options.length === 0 && !hasChildren) return null;
  const disabled = editingSection != null && editingSection !== heading;
  const isEditingSection = editingSection === heading;
  const editable = renderEdit != null || onEdit != null;

  const paddingClass = compact
    ? editable || isEditingSection
      ? "pt-2 px-3 pb-3"
      : "p-3"
    : editable || isEditingSection
      ? "pt-4 px-6 pb-6"
      : "p-6";

  return (
    <div
      data-testid={testId}
      className={classNames(
        "flex flex-col items-start min-w-0 w-full max-w-full h-full",
        className,
        `rounded-lg ${paddingClass}`,
        linked ? "border-2 border-extended-pgadarknavy-brand-color" : "border border-sys-brand-outline",
        disabled ? "opacity-50" : ""
      )}
    >
      <div
        className={classNames("flex flex-col w-full min-w-0 max-w-full", hasChildren && "flex-1 min-h-0")}
        data-testid={heading}
      >
        {heading && (
          <div className="flex justify-between items-center">
            <div className={classNames(editable ? "my-2" : "mb-2", "min-w-0")}>
              <p className="text-title-large-bold font-serif" data-testid={`details-card-${heading}-heading`}>
                {heading}
              </p>
              {caption ? (
                <p
                  className="mt-1 text-body-medium text-sys-brand-on-surface-variant"
                  data-testid="details-card-caption"
                >
                  {caption}
                </p>
              ) : null}
            </div>
            {editable && !isEditingSection && <IconButton name="edit" title="Edit" onClick={onEdit} skipMargin />}
          </div>
        )}
        {!heading && caption ? (
          <p className="mb-2 text-body-medium text-sys-brand-on-surface-variant" data-testid="details-card-caption">
            {caption}
          </p>
        ) : null}
        {editable && isEditingSection && renderEdit ? (
          <div className="mt-4 mb-2">{renderEdit()}</div>
        ) : (
          <>
            {options.length > 0 ? (
              <div className="flex flex-col w-full min-w-0 max-w-full">
                {options.map((item: SelectOptions, index) => {
                  return (
                    <div key={index} className="flex items-start">
                      <ListItem {...item} iconBackground={true} linked={linked} />
                      {item.renderActions && item.renderActions()}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {hasChildren ? (
              <div className="flex flex-1 flex-col w-full min-w-0 max-w-full min-h-0">{children}</div>
            ) : null}
          </>
        )}
        {actionButtons}
      </div>
    </div>
  );
}
